// P5 · Parada (documento 07). Entregar (completo o parcial), cobrar y dejar comprobante, o registrar por qué no.
import { Boton, CampoNumero, Chip, cx, HojaInferior, Icono, Tarjeta, type TonoChip, useToast } from '@reparto/ui';
import {
  type ComprobanteImprimible,
  enlaceNavegar,
  ESTADO_COMPROBANTE_ETIQUETA,
  formatoGs,
  formatoHora,
  formatoMiles,
  MEDIO_COBRO_ETIQUETA,
  MEDIOS_COBRO,
  type MedioCobro,
  montoEsperado,
  MOTIVO_NO_ENTREGA_ETIQUETA,
  type MotivoNoEntrega,
  hoyISO,
  TIPO_DOCUMENTO_FISCAL_ETIQUETA,
  type TipoComprobante,
} from '@reparto/shared/nucleo';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { entregar, llegada, noEntregar, type Posicion } from '../datos/acciones';
import {
  armarDocumento,
  type DocumentoArmado,
  documentoDePedido,
  guardarDocumento,
  planDe,
  saleAlEntregar,
  usadosLocales,
} from '../datos/facturacion';
import { guardarArchivo } from '../datos/archivos';
import { db, type Sesion } from '../datos/db';
import { useEstado } from '../datos/ganchos';
import type { Paquete, Parada as TipoParada } from '../datos/tipos';
import { aplicar, destinoParada } from '../datos/vista';
import { useVivo } from '../datos/vivo';
import {
  avisoImpresion,
  type ContextoComprobante,
  imprimirComprobante,
  imprimirComprobanteFiscal,
  leerConfiguracionImpresora,
} from '../impresion';
import { Firma, type FirmaHecha } from '../componentes/Firma';
import { EsqueletoParada } from '../componentes/Esqueletos';
import { Marco, Vacio } from '../componentes/Marco';
import { AvisarDeposito } from '../componentes/AvisarDeposito';
import { ubicacionCliente, ventana } from '../componentes/formato';
import { camara, type Foto } from '../servicios/camara';
import { gps } from '../servicios/gps';

const ICONO_MEDIO: Record<MedioCobro, string> = {
  efectivo: 'payments',
  qr: 'qr_code_2',
  transferencia: 'account_balance',
  cuenta_corriente: 'menu_book',
};

async function posicionActual(): Promise<Posicion | undefined> {
  const l = await gps.actual(60_000);
  return l ? { lat: l.lat, lng: l.lng, precision_m: l.precision_m } : undefined;
}

/** Lo que dice el comprobante además de la parada: empresa, móvil y chofer. */
function contextoComprobante(vista: Paquete, sesion: Sesion | null): ContextoComprobante {
  return {
    empresa: sesion?.empresa.nombre ?? '',
    movil: vista.movil.nombre,
    chofer: vista.ruta?.chofer?.nombre ?? sesion?.usuario.nombre ?? '',
  };
}

export function Parada() {
  const { id } = useParams();
  const { vista, sesion } = useEstado();
  const parada = vista?.ruta?.paradas.find((p) => p.id === id);
  if (!vista) return <EsqueletoParada />;
  if (!parada) {
    return (
      <Marco titulo="Parada" volver="/ruta">
        <Vacio
          icono="location_off"
          titulo="No encontramos esta parada"
          texto="Puede que el depósito la haya sacado de tu ruta."
        />
      </Marco>
    );
  }
  const volver = vista.ruta!.estado === 'cerrada' ? '/cierre' : '/ruta';
  const contexto = contextoComprobante(vista, sesion);
  return parada.resultado === 'pendiente' && vista.ruta!.estado === 'en_curso' ? (
    <Entrega key={parada.id} vista={vista} parada={parada} volver={volver} contexto={contexto} />
  ) : (
    <Resuelta parada={parada} volver={volver} contexto={contexto} vista={vista} />
  );
}

function Cabecera({ parada, deposito }: { parada: TipoParada; deposito: { lat: number; lng: number } }) {
  const c = parada.cliente;
  const v = ventana(parada.pedido.ventana_desde, parada.pedido.ventana_hasta);
  return (
    <Tarjeta className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-stone-500 tabular-nums">
            {parada.pedido.numero_visible}
            {c.codigo ? ` · cliente ${c.codigo}` : ''}
          </div>
          <div className="text-lg leading-tight font-medium">{c.nombre}</div>
          {c.contacto ? <div className="text-sm text-stone-600 dark:text-stone-300">{c.contacto}</div> : null}
        </div>
        {c.telefono ? (
          <a
            href={`tel:${c.telefono}`}
            aria-label={`Llamar a ${c.nombre}`}
            className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/60 dark:text-primary-200"
          >
            <Icono nombre="call" />
          </a>
        ) : null}
        <a
          href={enlaceNavegar(destinoParada(parada, deposito))}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Navegar hasta el cliente"
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-amber-400 text-stone-900"
        >
          <Icono nombre="navigation" />
        </a>
      </div>
      {c.direccion ? (
        <div className="flex gap-2 text-sm">
          <Icono nombre="location_on" tamano={20} className="shrink-0 text-stone-400" />
          <span>
            {c.direccion}
            {c.referencia ? <span className="block text-stone-500 dark:text-stone-400">{c.referencia}</span> : null}
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {c.zona_nombre ? <Chip icono="map">{c.zona_nombre}</Chip> : null}
        {v ? (
          <Chip tono={parada.ventana_en_riesgo ? 'peligro' : 'neutro'} icono="schedule">
            {v}
          </Chip>
        ) : null}
        {parada.llegada_en ? (
          <Chip tono="info" icono="where_to_vote">
            Llegó {formatoHora(parada.llegada_en)}
          </Chip>
        ) : null}
      </div>
      {parada.pedido.observaciones ? (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          {parada.pedido.observaciones}
        </p>
      ) : null}
    </Tarjeta>
  );
}

function Entrega({
  vista,
  parada,
  volver,
  contexto,
}: {
  vista: Paquete;
  parada: TipoParada;
  volver: string;
  contexto: ContextoComprobante;
}) {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const cfg = vista.configuracion;
  const ruta = vista.ruta!;

  const [cantidades, setCantidades] = useState<Record<string, number>>(() =>
    Object.fromEntries(parada.items.map((i) => [i.id, i.bultos])),
  );
  const medios = MEDIOS_COBRO.filter(
    (m) => cfg.cobro.medios[m] && (m !== 'cuenta_corriente' || parada.cliente.permite_cuenta_corriente),
  );
  const [medio, setMedio] = useState<MedioCobro>(
    medios.includes(parada.pedido.cobro) ? parada.pedido.cobro : (medios[0] ?? 'efectivo'),
  );
  const [montoEditado, setMontoEditado] = useState<number | null>(null);
  const [referencia, setReferencia] = useState('');
  const tipos: TipoComprobante[] =
    cfg.entrega.comprobante === 'ninguno'
      ? []
      : cfg.entrega.comprobante === 'firma_o_foto'
        ? ['firma', 'foto']
        : [cfg.entrega.comprobante];
  const [pestana, setPestana] = useState<TipoComprobante | null>(tipos[0] ?? null);
  const [firma, setFirma] = useState<FirmaHecha | null>(null);
  const [foto, setFoto] = useState<Foto | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [hoja, setHoja] = useState(false);
  const [motivo, setMotivo] = useState<Exclude<MotivoNoEntrega, 'sin_visitar'> | null>(null);
  const [motivoTexto, setMotivoTexto] = useState('');

  const entregados = parada.items.reduce((s, i) => s + (cantidades[i.id] ?? 0), 0);
  const cargados = parada.items.reduce((s, i) => s + i.bultos, 0);
  const esperado = useMemo(
    () =>
      montoEsperado(
        parada.items.map((i) => ({ bultosEntregados: cantidades[i.id] ?? 0, precioBulto: i.precio_bulto })),
      ),
    [cantidades, parada.items],
  );
  const monto = cfg.cobro.permitir_monto_distinto && montoEditado !== null ? montoEditado : esperado;
  const cc = medio === 'cuenta_corriente';
  const pideReferencia = medio === 'qr' || medio === 'transferencia';
  const faltaReferencia = medio === 'qr' && cfg.cobro.referencia_obligatoria_qr && !referencia.trim();
  const comprobante: TipoComprobante | null = firma && (pestana === 'firma' || !foto) ? 'firma' : foto ? 'foto' : null;
  const faltaComprobante = tipos.length > 0 && !comprobante;
  const ubic = ubicacionCliente(parada.cliente);

  useEffect(() => () => void (foto && URL.revokeObjectURL(foto.url)), [foto]);

  async function confirmar() {
    if (entregados === 0) return mostrar('Si no entregás nada, tocá "No se pudo entregar"', { tipo: 'error' });
    if (faltaComprobante)
      return mostrar(
        tipos.length > 1 ? 'Falta la firma o la foto' : tipos[0] === 'firma' ? 'Falta la firma' : 'Falta la foto',
        { tipo: 'error' },
      );
    if (faltaReferencia) return mostrar('Anotá la referencia del pago QR', { tipo: 'error' });
    setEnviando(true);
    try {
      const posicion = await posicionActual();
      const archivos: string[] = [];
      if (comprobante === 'firma' && firma)
        archivos.push(
          await guardarArchivo({
            blob: firma.blob,
            tipo: 'firma',
            mime: 'image/png',
            parada_id: parada.id,
            ancho: firma.ancho,
            alto: firma.alto,
          }),
        );
      if (foto)
        archivos.push(
          await guardarArchivo({
            blob: foto.blob,
            tipo: 'foto_entrega',
            mime: 'image/jpeg',
            parada_id: parada.id,
            ancho: foto.ancho,
            alto: foto.alto,
          }),
        );
      // Facturación (decisión D57): con la factura "al entregar" o "al cobrar", el documento sale ahora con lo entregado,
      // numerado con la reserva del móvil (o provisorio). Si el pedido ya lo tenía, se reimprime ese.
      const fact = vista.facturacion;
      const plan = planDe(fact, parada.pedido.cobro);
      const cobrado = cc ? 0 : monto;
      const sale = !!fact && !!plan && saleAlEntregar(plan, medio, cobrado);
      const yaTenia = sale
        ? documentoDePedido(parada.pedido.id, fact, await db.comprobantes.get(parada.pedido.id))
        : null;
      let armado: DocumentoArmado | null = null;
      if (sale && !yaTenia) {
        armado = armarDocumento({
          f: fact!,
          plan: plan!,
          parada,
          cantidades,
          usadosLocales: await usadosLocales(),
          hoy: hoyISO(),
        });
        await guardarDocumento(parada.pedido.id, armado);
      }
      const op = await entregar(parada.id, {
        items: parada.items.map((i) => ({ pedido_item_id: i.id, bultos_entregados: cantidades[i.id] ?? 0 })),
        cobro: { medio, monto_cobrado: cobrado, referencia: pideReferencia ? referencia.trim() || null : null },
        comprobante,
        archivosLocales: archivos,
        observaciones: observaciones.trim() || null,
        posicion,
        ...(armado && { documento: armado.documento }),
      });
      mostrar(entregados < cargados ? 'Entrega parcial guardada' : 'Entrega guardada', { tipo: 'ok' });
      const fiscal = armado?.comprobante ?? yaTenia;
      if (fiscal && leerConfiguracionImpresora().imprimirAlEntregar) {
        void imprimirComprobanteFiscal(fiscal).then((r) => {
          if (r.via !== 'impresora') mostrar(avisoImpresion(r).mensaje, { tipo: avisoImpresion(r).tipo });
        });
      } else if (leerConfiguracionImpresora().imprimirAlEntregar) {
        // La parada resuelta, con la misma regla que la vista local. Se imprime sin esperar: nada traba la entrega.
        const copia = structuredClone(vista);
        const resuelta = aplicar(copia, op) ? copia.ruta?.paradas.find((p) => p.id === parada.id) : undefined;
        if (resuelta)
          void imprimirComprobante(resuelta, contexto).then((r) => {
            if (r.via !== 'impresora') mostrar(avisoImpresion(r).mensaje, { tipo: avisoImpresion(r).tipo });
          });
      }
      siguiente();
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarNoEntrega() {
    if (!motivo) return;
    if (motivo === 'otro' && !motivoTexto.trim()) return mostrar('Contá qué pasó', { tipo: 'error' });
    setEnviando(true);
    try {
      await noEntregar(parada.id, motivo, motivoTexto.trim() || undefined, await posicionActual());
      setHoja(false);
      mostrar('Guardado: no se entregó', { tipo: 'info' });
      siguiente();
    } finally {
      setEnviando(false);
    }
  }

  /** Después de resolver: a la siguiente pendiente o a la lista. */
  function siguiente() {
    const prox = ruta.paradas.find((p) => p.resultado === 'pendiente' && p.id !== parada.id);
    navegar(prox ? '/ruta' : '/cierre', { replace: true });
  }

  // En pantallas anchas los dos botones van en una fila a la derecha, debajo del cobro y el comprobante.
  const pie = (
    <div className="space-y-2 pb-1 lg:flex lg:flex-row-reverse lg:items-center lg:gap-3 lg:space-y-0">
      <Boton
        bloque
        tamano="lg"
        icono="task_alt"
        cargando={enviando}
        onClick={() => void confirmar()}
        className="lg:w-auto lg:min-w-80"
      >
        {entregados < cargados && entregados > 0
          ? `Confirmar entrega parcial (${entregados}/${cargados})`
          : 'Confirmar entrega'}
      </Boton>
      <Boton
        bloque
        variante="peligro"
        icono="block"
        disabled={enviando}
        onClick={() => setHoja(true)}
        className="lg:w-auto"
      >
        No se pudo entregar
      </Boton>
    </div>
  );

  return (
    <Marco
      titulo={`Parada ${parada.orden} de ${ruta.paradas.length}`}
      subtitulo={parada.cliente.nombre}
      paso="entrega"
      volver={volver}
      pie={pie}
      ancho="amplio"
    >
      {/* Celular y tablet vertical: una columna. Pantallas anchas: cliente y bultos | cobro y comprobante. */}
      <div className="space-y-3 p-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0">
        <div className="space-y-3">
          <Cabecera parada={parada} deposito={ruta.deposito.ubicacion} />

          {!parada.llegada_en ? (
            <Boton
              bloque
              variante="secundario"
              icono="where_to_vote"
              onClick={() => void posicionActual().then((p) => llegada(parada.id, p))}
            >
              Llegué al cliente
            </Boton>
          ) : null}

          <AvisarDeposito paradaId={parada.id} />

          {ubic !== 'verificada' && cfg.entrega.fijar_gps_cliente ? (
            <p className="flex gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
              <Icono nombre="add_location_alt" className="shrink-0" />
              Este cliente no tiene la ubicación verificada: al confirmar se guarda la posición de tu celular.
            </p>
          ) : null}

          <section>
            <h2 className="mb-2 flex items-baseline justify-between px-1 text-sm font-medium text-stone-600 dark:text-stone-300">
              <span>Bultos a entregar</span>
              <span className="tabular-nums">
                {entregados} de {cargados}
              </span>
            </h2>
            <Tarjeta relleno="none" className="divide-y divide-stone-100 dark:divide-stone-800">
              {parada.items.map((i) => (
                <div key={i.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px]">{i.nombre}</div>
                    <div className="text-xs text-stone-500 tabular-nums">
                      {i.codigo} · {i.bultos} {i.unidad} · {formatoGs(i.precio_bulto)}
                    </div>
                    {/* Salió en parte del depósito (decisión D54): lo que pidió y por qué no subió todo. */}
                    {i.bultos_pedidos != null && i.bultos < i.bultos_pedidos ? (
                      <div className="text-xs text-amber-700 dark:text-amber-300">
                        Pidió {i.bultos_pedidos}
                        {i.motivo_carga ? ` · no subió: ${i.motivo_carga.toLowerCase()}` : ''}
                      </div>
                    ) : null}
                  </div>
                  {i.bultos === 0 ? (
                    <Chip tono="aviso">No subió</Chip>
                  ) : cfg.entrega.permitir_parcial ? (
                    <CampoNumero
                      valor={cantidades[i.id] ?? 0}
                      onCambiar={(n) => setCantidades((x) => ({ ...x, [i.id]: n }))}
                      min={0}
                      max={i.bultos}
                      etiqueta={`Bultos de ${i.nombre}`}
                    />
                  ) : (
                    <span className="text-base font-semibold tabular-nums">{i.bultos}</span>
                  )}
                </div>
              ))}
            </Tarjeta>
          </section>
        </div>

        <div className="space-y-3">
          <section>
            <h2 className="mb-2 px-1 text-sm font-medium text-stone-600 dark:text-stone-300">Cobro</h2>
            <Tarjeta className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {medios.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMedio(m)}
                    aria-pressed={medio === m}
                    className={cx(
                      'flex h-12 items-center gap-2 rounded-2xl border px-3 text-sm font-medium',
                      medio === m
                        ? 'border-primary-600 bg-primary-50 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100'
                        : 'border-stone-200 dark:border-stone-700',
                    )}
                  >
                    <Icono nombre={ICONO_MEDIO[m]} relleno={medio === m} />
                    {MEDIO_COBRO_ETIQUETA[m]}
                  </button>
                ))}
              </div>
              {cc ? (
                <p className="rounded-xl bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
                  Queda a cuenta corriente: <b className="tabular-nums">{formatoGs(esperado)}</b>. No cobrás nada ahora.
                </p>
              ) : (
                <label className="block">
                  <span className="mb-1 block text-xs text-stone-500">
                    Monto cobrado (esperado {formatoGs(esperado)})
                  </span>
                  <div className="flex h-14 items-center rounded-2xl border border-stone-300 bg-white px-4 focus-within:border-primary-600 dark:border-stone-600 dark:bg-stone-900">
                    <span className="mr-2 text-stone-500">Gs.</span>
                    <input
                      inputMode="numeric"
                      readOnly={!cfg.cobro.permitir_monto_distinto}
                      value={formatoMiles(monto)}
                      onChange={(e) => setMontoEditado(Number(e.target.value.replace(/\D/g, '')) || 0)}
                      className="min-w-0 flex-1 bg-transparent text-2xl font-semibold tabular-nums outline-none"
                      aria-label="Monto cobrado"
                    />
                    {montoEditado !== null && montoEditado !== esperado ? (
                      <button
                        type="button"
                        onClick={() => setMontoEditado(null)}
                        className="text-xs text-primary-700 underline dark:text-primary-300"
                      >
                        Usar esperado
                      </button>
                    ) : null}
                  </div>
                  {monto !== esperado ? (
                    <span
                      className={cx(
                        'mt-1 block text-xs',
                        monto < esperado ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300',
                      )}
                    >
                      Diferencia {monto < esperado ? '−' : '+'}
                      {formatoGs(Math.abs(monto - esperado))}: se informa en la rendición.
                    </span>
                  ) : null}
                </label>
              )}
              {pideReferencia ? (
                <input
                  className="h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 dark:border-stone-600 dark:bg-stone-900"
                  placeholder={
                    medio === 'qr' && cfg.cobro.referencia_obligatoria_qr
                      ? 'Referencia del pago (obligatoria)'
                      : 'Referencia del pago (opcional)'
                  }
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  maxLength={80}
                />
              ) : null}
            </Tarjeta>
          </section>

          {tipos.length ? (
            <section>
              <h2 className="mb-2 flex items-baseline justify-between px-1 text-sm font-medium text-stone-600 dark:text-stone-300">
                <span>Comprobante</span>
                <span className="text-xs font-normal">
                  {tipos.length > 1
                    ? 'Obligatorio: firma o foto'
                    : tipos[0] === 'firma'
                      ? 'Firma obligatoria'
                      : 'Foto obligatoria'}
                </span>
              </h2>
              <Tarjeta className="space-y-3">
                {tipos.length > 1 ? (
                  <div className="grid grid-cols-2 rounded-full bg-stone-100 p-1 dark:bg-stone-800" role="tablist">
                    {tipos.map((t) => (
                      <button
                        key={t}
                        type="button"
                        role="tab"
                        aria-selected={pestana === t}
                        onClick={() => setPestana(t)}
                        className={cx(
                          'flex h-10 items-center justify-center gap-1 rounded-full text-sm font-medium',
                          pestana === t && 'bg-white shadow dark:bg-stone-700',
                        )}
                      >
                        <Icono nombre={t === 'firma' ? 'signature' : 'photo_camera'} tamano={20} />
                        {t === 'firma' ? 'Firma' : 'Foto'}
                        {(t === 'firma' ? firma : foto) ? (
                          <Icono nombre="check_circle" tamano={16} className="text-primary-600" />
                        ) : null}
                      </button>
                    ))}
                  </div>
                ) : null}
                {tipos.includes('firma') ? (
                  <div className={pestana === 'firma' ? '' : 'hidden'}>
                    <Firma onCambio={setFirma} />
                  </div>
                ) : null}
                {pestana === 'foto' ? (
                  foto ? (
                    <div className="space-y-2">
                      <img
                        src={foto.url}
                        alt="Foto de la entrega"
                        className="max-h-64 w-full rounded-2xl object-cover"
                      />
                      <Boton
                        bloque
                        variante="secundario"
                        icono="photo_camera"
                        onClick={() => void camara.sacarFoto().then((f) => f && setFoto(f))}
                      >
                        Repetir foto
                      </Boton>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void camara.sacarFoto().then((f) => f && setFoto(f))}
                      className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-stone-300 text-stone-500 dark:border-stone-600"
                    >
                      <Icono nombre="photo_camera" tamano={40} />
                      Sacar foto de la mercadería entregada
                    </button>
                  )
                ) : null}
              </Tarjeta>
            </section>
          ) : null}

          <section>
            <textarea
              className="min-h-20 w-full rounded-2xl border border-stone-300 bg-white p-3 text-[15px] dark:border-stone-600 dark:bg-stone-900"
              placeholder="Observaciones (opcional)"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              maxLength={500}
            />
          </section>
        </div>
      </div>

      <HojaInferior abierta={hoja} onCerrar={() => setHoja(false)} titulo="¿Por qué no se pudo entregar?">
        <div className="space-y-2">
          {cfg.entrega.motivos_no_entrega.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMotivo(m)}
              aria-pressed={motivo === m}
              className={cx(
                'flex h-12 w-full items-center gap-3 rounded-2xl border px-4 text-left',
                motivo === m
                  ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/40'
                  : 'border-stone-200 dark:border-stone-700',
              )}
            >
              <Icono
                nombre={motivo === m ? 'radio_button_checked' : 'radio_button_unchecked'}
                className={motivo === m ? 'text-primary-700 dark:text-primary-300' : 'text-stone-400'}
              />
              {MOTIVO_NO_ENTREGA_ETIQUETA[m]}
            </button>
          ))}
          {motivo === 'otro' ? (
            <input
              className="h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 dark:border-stone-600 dark:bg-stone-900"
              placeholder="Contá qué pasó"
              value={motivoTexto}
              onChange={(e) => setMotivoTexto(e.target.value)}
              maxLength={200}
              autoFocus
            />
          ) : null}
          <p className="px-1 pt-1 text-xs text-stone-500">Los {cargados} bultos vuelven al depósito.</p>
          <Boton
            bloque
            tamano="lg"
            variante="peligro"
            icono="block"
            disabled={!motivo}
            cargando={enviando}
            onClick={() => void confirmarNoEntrega()}
          >
            Confirmar: no se entregó
          </Boton>
        </div>
      </HojaInferior>
    </Marco>
  );
}

const TONO_RESULTADO: Record<string, { texto: string; tono: TonoChip }> = {
  entregada: { texto: 'Entregada', tono: 'exito' },
  parcial: { texto: 'Entrega parcial', tono: 'aviso' },
  no_entregada: { texto: 'No entregada', tono: 'peligro' },
  pendiente: { texto: 'Pendiente', tono: 'neutro' },
};

function Resuelta({
  parada,
  volver,
  contexto,
  vista,
}: {
  parada: TipoParada;
  volver: string;
  contexto: ContextoComprobante;
  vista: Paquete;
}) {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const [imprimiendo, setImprimiendo] = useState(false);
  const imprimible = parada.resultado === 'entregada' || parada.resultado === 'parcial';
  // El documento del pedido (decisión D57): el del servidor o el que armó el celular al entregar.
  const local = useVivo(() => db.comprobantes.get(parada.pedido.id), [parada.pedido.id], undefined);
  const fiscal = imprimible ? documentoDePedido(parada.pedido.id, vista.facturacion, local) : null;
  async function imprimir() {
    setImprimiendo(true);
    try {
      // Desde un toque se puede pedir elegir la impresora si se había desconectado.
      const r = fiscal
        ? await imprimirComprobanteFiscal(fiscal, { permitirElegir: true })
        : await imprimirComprobante(parada, contexto, { permitirElegir: true });
      const a = avisoImpresion(r);
      mostrar(a.mensaje, { tipo: a.tipo });
    } finally {
      setImprimiendo(false);
    }
  }
  const locales = useVivo(() => db.archivos.where('parada_id').equals(parada.id).toArray(), [parada.id], []);
  const urls = useMemo(
    () => locales.map((a) => ({ id: a.id, tipo: a.tipo, url: URL.createObjectURL(a.blob), estado: a.estado })),
    [locales],
  );
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u.url)), [urls]);
  const r = TONO_RESULTADO[parada.resultado]!;
  const entregados = parada.items.reduce((s, i) => s + (i.bultos_entregados ?? 0), 0);
  const cargados = parada.items.reduce((s, i) => s + i.bultos, 0);
  return (
    <Marco
      titulo={parada.cliente.nombre}
      subtitulo={parada.pedido.numero_visible}
      paso="entrega"
      volver={volver}
      pie={
        <div className="flex gap-2 pb-1">
          {imprimible ? (
            <Boton
              tamano="lg"
              icono="print"
              cargando={imprimiendo}
              onClick={() => void imprimir()}
              className="min-w-0 flex-1"
            >
              {fiscal
                ? `Imprimir ${TIPO_DOCUMENTO_FISCAL_ETIQUETA[fiscal.tipo].toLowerCase()}`
                : 'Imprimir comprobante'}
            </Boton>
          ) : null}
          <Boton
            tamano="lg"
            variante="secundario"
            icono="arrow_back"
            onClick={() => navegar(volver)}
            className={imprimible ? 'shrink-0' : 'w-full'}
          >
            Volver
          </Boton>
        </div>
      }
    >
      <div className="space-y-3 p-4">
        <Tarjeta className="space-y-2">
          <div className="flex items-center gap-2">
            <Chip tono={r.tono}>{r.texto}</Chip>
            {parada.resuelta_en ? (
              <span className="text-sm text-stone-500">a las {formatoHora(parada.resuelta_en)}</span>
            ) : null}
          </div>
          {parada.motivo ? (
            <p className="text-sm">
              Motivo: <b>{MOTIVO_NO_ENTREGA_ETIQUETA[parada.motivo]}</b>
              {parada.motivo_texto ? ` · ${parada.motivo_texto}` : ''}
            </p>
          ) : null}
          {parada.pendiente_sync ? (
            <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
              <Icono nombre="cloud_upload" tamano={16} /> Guardado en el celular. Se envía cuando haya señal.
            </p>
          ) : null}
        </Tarjeta>
        <AvisarDeposito paradaId={parada.id} />
        {fiscal ? <DocumentoPedido c={fiscal} /> : null}
        <Tarjeta relleno="none" className="divide-y divide-stone-100 dark:divide-stone-800">
          <div className="flex justify-between px-4 py-2 text-sm font-medium">
            <span>Bultos entregados</span>
            <span className="tabular-nums">
              {entregados} de {cargados}
            </span>
          </div>
          {parada.items.map((i) => (
            <div key={i.id} className="flex justify-between gap-3 px-4 py-2 text-sm">
              <span className="min-w-0 truncate">{i.nombre}</span>
              <span className="shrink-0 tabular-nums">
                {i.bultos_entregados ?? 0}/{i.bultos}
                {i.bultos_devueltos ? (
                  <span className="text-rose-600 dark:text-rose-300"> · vuelven {i.bultos_devueltos}</span>
                ) : null}
              </span>
            </div>
          ))}
        </Tarjeta>
        {parada.cobro ? (
          <Tarjeta className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>{MEDIO_COBRO_ETIQUETA[parada.cobro.medio]}</span>
              <b className="tabular-nums">
                {formatoGs(
                  parada.cobro.medio === 'cuenta_corriente' ? parada.cobro.monto_a_cuenta : parada.cobro.monto_cobrado,
                )}
              </b>
            </div>
            {parada.cobro.monto_cobrado !== parada.cobro.monto_esperado && parada.cobro.medio !== 'cuenta_corriente' ? (
              <div className="text-xs text-stone-500">Esperado {formatoGs(parada.cobro.monto_esperado)}</div>
            ) : null}
            {parada.cobro.referencia ? (
              <div className="text-xs text-stone-500">Ref. {parada.cobro.referencia}</div>
            ) : null}
          </Tarjeta>
        ) : null}
        {urls.length ? (
          <div className="grid grid-cols-2 gap-2">
            {urls.map((u) => (
              <figure
                key={u.id}
                className="overflow-hidden rounded-2xl border border-stone-200 bg-white dark:border-stone-700"
              >
                <img src={u.url} alt={u.tipo === 'firma' ? 'Firma' : 'Foto'} className="h-32 w-full object-contain" />
                <figcaption className="px-2 py-1 text-xs text-stone-500">
                  {u.tipo === 'firma' ? 'Firma' : 'Foto'} · {u.estado === 'subido' ? 'enviada' : 'por enviar'}
                </figcaption>
              </figure>
            ))}
          </div>
        ) : parada.archivos.length ? (
          <p className="text-sm text-stone-500">
            <Icono nombre="attach_file" tamano={16} /> {parada.archivos.length} comprobante(s) en el servidor.
          </p>
        ) : null}
        {parada.observaciones ? (
          <p className="text-sm text-stone-600 dark:text-stone-300">Obs.: {parada.observaciones}</p>
        ) : null}
      </div>
    </Marco>
  );
}

/** El documento de la venta del pedido (decisión D57): número, estado y total; el provisorio avisa que se numera. */
export function DocumentoPedido({ c }: { c: ComprobanteImprimible }) {
  return (
    <Tarjeta className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">
          {TIPO_DOCUMENTO_FISCAL_ETIQUETA[c.tipo]} {c.numero_visible ?? ''}
        </span>
        <Chip tono={c.estado === 'emitido' ? 'exito' : 'aviso'}>{ESTADO_COMPROBANTE_ETIQUETA[c.estado]}</Chip>
      </div>
      <div className="flex justify-between text-sm">
        <span>Total</span>
        <b className="tabular-nums">{formatoGs(c.totales.total)}</b>
      </div>
      {c.estado === 'provisorio' ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Se numera al sincronizar: después reimprimilo para dejarle el definitivo.
        </p>
      ) : null}
    </Tarjeta>
  );
}
